import React, { useState, useMemo } from 'react';
import { Search, X, Wrench, Factory, Briefcase, ShoppingBag, Laptop, Store, BookmarkPlus } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';

// Industry vocab matches the seller wizard's WIZARD_INDUSTRIES + the
// Polymet marketplace-browse.tsx mock. Display label / value / icon.
const INDUSTRIES = [
  { value: 'trades_construction',   label: 'Trades & Construction', icon: Wrench },
  { value: 'manufacturing',         label: 'Manufacturing',         icon: Factory },
  { value: 'professional_services', label: 'Professional Services', icon: Briefcase },
  { value: 'retail',                label: 'Retail',                icon: ShoppingBag },
  { value: 'technology',            label: 'Technology',            icon: Laptop },
];

const PROVINCES = [
  'Ontario', 'Quebec', 'British Columbia', 'Alberta', 'Manitoba',
  'Saskatchewan', 'Nova Scotia', 'New Brunswick', 'Newfoundland and Labrador',
  'Prince Edward Island', 'Yukon', 'Northwest Territories', 'Nunavut',
];

const PRICE_BUCKETS    = ['under_500k', '500k_to_1m', '1m_to_5m', '5m_plus'];
const REVENUE_BUCKETS  = ['under_500k', '500k_to_1m', '1m_to_5m', '5m_plus'];
const TIMELINES        = ['6_12_months', '1_2_years', '3_plus_years'];

const BUCKET_LABELS = {
  under_500k:    'Under $500K',
  '500k_to_1m':  '$500K – $1M',
  '1m_to_5m':    '$1M – $5M',
  '5m_plus':     '$5M+',
};
const TIMELINE_LABELS = {
  '6_12_months':  '6–12 Months',
  '1_2_years':    '1–2 Years',
  '3_plus_years': '3+ Years',
};

const formatCurrency = (value) => {
  if (value == null) return 'Make Offer';
  const num = Number(value);
  if (Number.isNaN(num) || num === 0) return 'Make Offer';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
};

export default function MarketplaceBrowsePage({
  listings = [],
  totalCount = 0,
  loading = false,
  persona = 'BUYER',
  userName = '',
  userInitials = '',
  planProgress = { completed: 0, total: 5, percentage: 0 },
  actionPlanBadge = 0,
  onApplyFilters,
  onSaveSearch, // ({ name, alert_frequency, filters }) => Promise<void>
}) {
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveBusy,      setSaveBusy]      = useState(false);
  const [saveError,     setSaveError]     = useState('');
  const [searchQuery,        setSearchQuery]        = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedProvinces,  setSelectedProvinces]  = useState([]);
  const [priceRange,         setPriceRange]         = useState('');
  const [revenueRange,       setRevenueRange]       = useState('');
  const [timeline,           setTimeline]           = useState('');
  const [sortBy,             setSortBy]             = useState('newest');

  const fireFilters = (next) => {
    onApplyFilters?.({
      q:                    next.searchQuery ?? searchQuery,
      industry:             next.selectedIndustries ?? selectedIndustries,
      province:             next.selectedProvinces ?? selectedProvinces,
      asking_price_range:   next.priceRange ?? priceRange,
      revenue_range:        next.revenueRange ?? revenueRange,
      timeline:             next.timeline ?? timeline,
      sort:                 next.sortBy ?? sortBy,
    });
    if (typeof window !== 'undefined' && window.ahoy?.track) {
      try { window.ahoy.track('succeedo_marketplace_search'); } catch (_e) {}
    }
  };

  const toggle = (setter, current, value) => {
    const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    setter(updated);
    return updated;
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedIndustries([]);
    setSelectedProvinces([]);
    setPriceRange('');
    setRevenueRange('');
    setTimeline('');
    fireFilters({ searchQuery: '', selectedIndustries: [], selectedProvinces: [], priceRange: '', revenueRange: '', timeline: '' });
  };

  const activeFilters = useMemo(() => ([
    ...selectedIndustries.map((v) => ({ kind: 'industry', value: v, label: INDUSTRIES.find((i) => i.value === v)?.label || v })),
    ...selectedProvinces.map((v) => ({ kind: 'province', value: v, label: v })),
    ...(priceRange   ? [{ kind: 'price',    value: priceRange,   label: `Price ${BUCKET_LABELS[priceRange]   || priceRange}` }] : []),
    ...(revenueRange ? [{ kind: 'revenue',  value: revenueRange, label: `Revenue ${BUCKET_LABELS[revenueRange] || revenueRange}` }] : []),
    ...(timeline     ? [{ kind: 'timeline', value: timeline,     label: TIMELINE_LABELS[timeline] || timeline }] : []),
  ]), [selectedIndustries, selectedProvinces, priceRange, revenueRange, timeline]);

  const removeFilter = (filter) => {
    if (filter.kind === 'industry') {
      const next = selectedIndustries.filter((v) => v !== filter.value);
      setSelectedIndustries(next);
      fireFilters({ selectedIndustries: next });
    } else if (filter.kind === 'province') {
      const next = selectedProvinces.filter((v) => v !== filter.value);
      setSelectedProvinces(next);
      fireFilters({ selectedProvinces: next });
    } else if (filter.kind === 'price')    { setPriceRange('');   fireFilters({ priceRange: '' }); }
    else if   (filter.kind === 'revenue')  { setRevenueRange(''); fireFilters({ revenueRange: '' }); }
    else if   (filter.kind === 'timeline') { setTimeline('');     fireFilters({ timeline: '' }); }
  };

  return (
    <AppLayout
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      planProgress={planProgress}
      actionPlanBadge={actionPlanBadge}
    >
      <div className="flex">
        <aside className="w-[240px] border-r border-border bg-card/50 min-h-screen p-6 space-y-6 overflow-y-auto">
          <FilterSection title="Industry">
            <div className="space-y-2">
              {INDUSTRIES.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedIndustries.includes(value)}
                    onChange={() => {
                      const next = toggle(setSelectedIndustries, selectedIndustries, value);
                      fireFilters({ selectedIndustries: next });
                    }}
                    className="w-4 h-4 rounded border-border text-[hsl(var(--succeedo-orange))] focus:ring-[hsl(var(--succeedo-orange))] focus:ring-offset-0"
                  />
                  <span className="text-[13px] text-foreground">{label}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Asking Price">
            <SelectFilter value={priceRange} onChange={(v) => { setPriceRange(v); fireFilters({ priceRange: v }); }} options={PRICE_BUCKETS} />
          </FilterSection>

          <FilterSection title="Annual Revenue">
            <SelectFilter value={revenueRange} onChange={(v) => { setRevenueRange(v); fireFilters({ revenueRange: v }); }} options={REVENUE_BUCKETS} />
          </FilterSection>

          <FilterSection title="Province">
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {PROVINCES.map((province) => (
                <label key={province} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedProvinces.includes(province)}
                    onChange={() => {
                      const next = toggle(setSelectedProvinces, selectedProvinces, province);
                      fireFilters({ selectedProvinces: next });
                    }}
                    className="w-4 h-4 rounded border-border text-[hsl(var(--succeedo-orange))] focus:ring-[hsl(var(--succeedo-orange))] focus:ring-offset-0"
                  />
                  <span className="text-[13px] text-foreground">{province}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Timeline">
            <div className="flex flex-wrap gap-2">
              {TIMELINES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    const next = timeline === t ? '' : t;
                    setTimeline(next);
                    fireFilters({ timeline: next });
                  }}
                  className={`px-3 py-1.5 rounded-full text-[12px] transition-colors ${
                    timeline === t
                      ? 'bg-[hsl(var(--succeedo-orange))] text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {TIMELINE_LABELS[t]}
                </button>
              ))}
            </div>
          </FilterSection>
        </aside>

        <main className="flex-1 p-8">
          <div className="mb-8">
            <h1 className="font-serif text-[42px] font-light tracking-[-0.02em] text-foreground mb-2">
              Marketplace
            </h1>
            <p className="text-sm text-muted-foreground">
              Confidential SMB businesses for sale across Canada.
            </p>
          </div>

          <div className="mb-6 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                fireFilters({ searchQuery: e.target.value });
              }}
              placeholder="Search by industry, province, or keywords…"
              className="w-full bg-background border border-input rounded-full pl-12 pr-4 py-3 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
            />
          </div>

          {activeFilters.length > 0 && (
            <div className="mb-6 flex flex-wrap items-center gap-2">
              {activeFilters.map((filter, i) => (
                <button
                  key={`${filter.kind}-${filter.value}-${i}`}
                  type="button"
                  onClick={() => removeFilter(filter)}
                  className="flex items-center gap-2 bg-muted text-foreground px-3 py-1.5 rounded-full text-[12px] hover:bg-muted/80 transition-colors"
                >
                  {filter.label}
                  <X className="w-3 h-3" />
                </button>
              ))}
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-[12px] text-[hsl(var(--succeedo-orange))] hover:underline ml-2"
              >
                Clear all
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <div className="text-sm text-muted-foreground">
              {loading ? 'Loading…' : `${totalCount} ${totalCount === 1 ? 'listing' : 'listings'} found`}
            </div>
            <div className="flex items-center gap-2">
              {/* D16 — Save this search. The button stays enabled with no
                  filters too: a "Save All Listings" search is a valid
                  catch-all alert. */}
              <button
                type="button"
                onClick={() => { setSaveError(''); setSaveModalOpen(true); }}
                className="border border-border text-foreground rounded-lg px-3 py-2 text-[13px] hover:bg-accent transition-colors inline-flex items-center gap-2"
              >
                <BookmarkPlus className="w-4 h-4" />
                Save this search
              </button>
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); fireFilters({ sortBy: e.target.value }); }}
                className="bg-background border border-input rounded-lg px-3 py-2 text-[13px] text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
              >
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="best_match">Best Match</option>
              </select>
            </div>
          </div>

          {listings.length === 0 && !loading ? (
            <EmptyState onClear={clearAllFilters} hasFilters={activeFilters.length > 0} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing) => <MarketplaceCard key={listing.id} listing={listing} />)}
            </div>
          )}
        </main>
      </div>

      {saveModalOpen && (
        <SaveSearchModal
          busy={saveBusy}
          error={saveError}
          // Filter snapshot at modal-open time. Only one industry / province
          // gets persisted per saved search — server-side SavedSearch.filters
          // is a flat hash, and the alert worker matches a single value per
          // key. If the buyer has 3 industries selected, we save the first
          // one and warn them; this matches the Polymet "Tech in Ontario"
          // single-bucket vocabulary.
          filtersSnapshot={{
            q:                    searchQuery || null,
            industry:             selectedIndustries[0] || null,
            province:             selectedProvinces[0]  || null,
            asking_price_range:   priceRange   || null,
            revenue_range:        revenueRange || null,
            timeline:             timeline     || null,
          }}
          multipleIndustriesSelected={selectedIndustries.length > 1}
          multipleProvincesSelected={selectedProvinces.length > 1}
          onClose={() => setSaveModalOpen(false)}
          onSave={async (payload) => {
            setSaveError('');
            setSaveBusy(true);
            try {
              await onSaveSearch?.(payload);
              setSaveModalOpen(false);
            } catch (e) {
              setSaveError(e?.message || 'Could not save search.');
            } finally {
              setSaveBusy(false);
            }
          }}
        />
      )}
    </AppLayout>
  );
}

function SaveSearchModal({
  busy,
  error,
  filtersSnapshot,
  multipleIndustriesSelected,
  multipleProvincesSelected,
  onClose,
  onSave,
}) {
  const defaultName = useMemo(() => {
    const parts = [];
    if (filtersSnapshot.industry) parts.push(String(filtersSnapshot.industry).replace(/_/g, ' '));
    if (filtersSnapshot.province) parts.push(filtersSnapshot.province);
    if (filtersSnapshot.asking_price_range) parts.push(BUCKET_LABELS[filtersSnapshot.asking_price_range] || filtersSnapshot.asking_price_range);
    if (filtersSnapshot.q) parts.push(`"${filtersSnapshot.q}"`);
    return parts.join(' · ') || 'All marketplace listings';
  }, [filtersSnapshot]);

  const [name,      setName]      = useState(defaultName);
  const [frequency, setFrequency] = useState('daily');

  // Build the cleaned filter payload — drop nulls so SavedSearch.filters
  // doesn't carry empty-string keys that the worker would treat as
  // active filters.
  const cleanedFilters = useMemo(() => {
    const out = {};
    Object.entries(filtersSnapshot).forEach(([k, v]) => { if (v) out[k] = v; });
    return out;
  }, [filtersSnapshot]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), alert_frequency: frequency, filters: cleanedFilters });
  };

  return (
    <div className="fixed inset-0 bg-foreground/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-[16px] p-6 max-w-md w-full space-y-5">
        <div>
          <h2 className="text-[18px] font-semibold text-foreground mb-1">Save this search</h2>
          <p className="text-[13px] text-muted-foreground">
            Get notified when matching businesses publish.
          </p>
        </div>

        {(multipleIndustriesSelected || multipleProvincesSelected) && (
          <div className="bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/30 rounded-lg p-3 text-[12px] text-foreground">
            Saved searches track one industry and one province. We'll save your first selection — adjust filters before saving if you want a different scope.
          </div>
        )}

        <div>
          <label className="block font-mono text-[11px] uppercase text-muted-foreground tracking-wide mb-2">
            SEARCH NAME
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            className="w-full bg-background border border-input rounded-lg px-3 py-2 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
          />
        </div>

        <div className="space-y-2">
          <label className="block font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
            ALERT FREQUENCY
          </label>
          {[
            { value: 'instant', label: 'Instant alerts',  blurb: 'Get notified as soon as a match publishes' },
            { value: 'daily',   label: 'Daily digest',    blurb: 'One email per day with all new matches' },
            { value: 'weekly',  label: 'Weekly digest',   blurb: 'One email per week with all new matches' },
          ].map((opt) => (
            <label key={opt.value} className="flex items-center gap-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="radio"
                name="frequency"
                value={opt.value}
                checked={frequency === opt.value}
                onChange={(e) => setFrequency(e.target.value)}
                className="w-4 h-4"
              />
              <div>
                <div className="text-[13px] font-medium text-foreground">{opt.label}</div>
                <div className="text-[12px] text-muted-foreground">{opt.blurb}</div>
              </div>
            </label>
          ))}
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-[13px] text-destructive">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="border border-border text-foreground rounded-full px-5 py-2 text-[14px] hover:bg-accent transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || !name.trim()}
            className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[14px] hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save search'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterSection({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3 tracking-wide">{title}</div>
      {children}
    </div>
  );
}

function SelectFilter({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-background border border-input rounded-lg px-3 py-2 text-[13px] text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
    >
      <option value="">All ranges</option>
      {options.map((bucket) => (
        <option key={bucket} value={bucket}>{BUCKET_LABELS[bucket] || bucket}</option>
      ))}
    </select>
  );
}

function MarketplaceCard({ listing }) {
  const industryDef = INDUSTRIES.find((i) => i.value === listing.industry);
  const Icon = industryDef?.icon || Store;
  const industryLabel = industryDef?.label || listing.industry || '—';
  const askingPrice = formatCurrency(listing.asking_price);
  const revenue = BUCKET_LABELS[listing.revenue_range_bucket] || '—';
  const employees = BUCKET_LABELS[listing.employee_count_bucket] || listing.employees || '—';
  const timelineLabel = TIMELINE_LABELS[listing.timeline] || listing.timeline || '—';

  return (
    <a
      href={`/succeedo/marketplace/${listing.id}`}
      className="bg-card border border-border rounded-[16px] p-6 hover:border-[hsl(var(--succeedo-orange))]/50 transition-all group block"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <Icon className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <div className="text-[14px] font-medium text-foreground group-hover:text-[hsl(var(--succeedo-orange))] transition-colors">
              {industryLabel}
            </div>
            <div className="text-[12px] text-muted-foreground">{listing.province || '—'}</div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <span className="font-mono text-[10px] uppercase text-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10 px-2 py-1 rounded">
          Confidential Listing
        </span>
      </div>

      <div className="text-[24px] font-semibold text-foreground mb-4">{askingPrice}</div>

      <dl className="space-y-2 mb-4">
        <Row label="Revenue"   value={revenue} />
        <Row label="EBITDA"    value={BUCKET_LABELS[listing.ebitda_range_bucket] || '—'} />
        <Row label="Employees" value={employees} />
      </dl>

      <div className="pt-4 border-t border-border">
        <span className="inline-block px-3 py-1 rounded-full bg-muted text-muted-foreground text-[11px]">
          {timelineLabel}
        </span>
      </div>
    </a>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium">{value}</span>
    </div>
  );
}

function EmptyState({ onClear, hasFilters }) {
  return (
    <div className="text-center py-16">
      <div className="text-muted-foreground mb-4">
        {hasFilters ? 'No matches with these filters' : 'No listings yet'}
      </div>
      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="border border-border text-foreground rounded-full px-5 py-[9px] text-sm hover:bg-accent transition-colors"
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}
